import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { CircularProgress } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { parse } from 'query-string'
import { PuiCheckbox } from '@pbt/pbt-ui-components'

import Features from '../../../constants/features'
import {
  getCurrentBusiness,
  getCurrentBusinessIsOmniChannel,
} from '../../../store/duck/businesses'
import { getCurrentClient } from '../../../store/duck/clients'
import { getFeatureToggle } from '../../../store/duck/constants'
import {
  fetchMostRecentTermsAndConditions,
  getMembershipIsPosFlow,
  getMembershipToken,
  getTermsAndConditions,
  getWellnessPlansIsLoading,
  updateAcceptedTcVersionId,
} from '../../../store/duck/wellnessPlans'
import { useNavigateWithQueryString } from '../../../utils'
import KioskScreen from '../KioskScreen'
import GlobalTermsAndConditions from './GlobalTermsAndConditions'
import OmnichannelWellnessPlanTermsAndConditions from './OmnichannelWellnessPlanTermsAndConditions'
import useHandleFreeSignUp from './useHandleFreeSignUp'
import { useKioskMembershipEnrollmentTracking } from './useKioskMembershipEnrollmentTracking'
import WellnessPlanTermsAndConditions from './WellnessPlanTermsAndConditions'

const useStyles = makeStyles(
  (theme) => ({
    title: {
      textAlign: 'left',
      padding: theme.spacing(2, 4, 0),
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2, 1, 0),
      },
    },
    checkbox: {
      margin: 'auto',
      fontSize: '1.4rem',
      fontWeight: 'bold',
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    },
  }),
  { name: 'MembershipTermsAndConditionsScreen' },
)

const MembershipTermsAndConditionsScreen = () => {
  const classes = useStyles()
  const navigateWithQueryString = useNavigateWithQueryString()
  const dispatch = useDispatch()
  const location = useLocation()
  const { businessId: businessIdParam } = parse(location.search)
  const { redirectUrl } = location.state || {}
  const { t } = useTranslation(['Common', 'Membership'])
  const { trackMembershipEnrollment } = useKioskMembershipEnrollmentTracking()
  const isCurrentBusinessOmniChannel = useSelector(
    getCurrentBusinessIsOmniChannel,
  )
  const isMembershipSignupTerminalEnabled = useSelector(
    getFeatureToggle(Features.MEMBERSHIP_SIGNUP_TERMINAL),
  )

  const client = useSelector(getCurrentClient)
  const currentBusiness = useSelector(getCurrentBusiness)

  const termsAndConditions = useSelector(getTermsAndConditions)
  const isLoading = useSelector(getWellnessPlansIsLoading)

  const token = useSelector(getMembershipToken)
  const isPosMembershipFlow = useSelector(getMembershipIsPosFlow)

  const { id } = currentBusiness || {}
  const businessId = businessIdParam || id

  const [scrolled, setScrolled] = useState(false)
  const [accepted, setAccepted] = useState(false)

  const searchParams = new URLSearchParams(location.search)
  const viewOnly = searchParams.get('view') === 'true'

  const { handler: handleFreeSignUp, isLoading: signUpIsLoading } =
    useHandleFreeSignUp()

  useEffect(() => {
    if (viewOnly) {
      return
    }

    trackMembershipEnrollment('TERMS_VIEWED')
  }, [trackMembershipEnrollment, viewOnly])

  useEffect(() => {
    dispatch(fetchMostRecentTermsAndConditions(businessId, token))
  }, [businessId, token, dispatch])

  const onProceed = () => {
    if (viewOnly) {
      if (redirectUrl) {
        navigateWithQueryString({
          url: redirectUrl,
          options: { replace: true },
        })
      }

      return
    }

    trackMembershipEnrollment('TERMS_CONTINUE_CLICKED')
    if (termsAndConditions?.tcVersionId) {
      dispatch(updateAcceptedTcVersionId(termsAndConditions.tcVersionId))
    }
    if (redirectUrl) {
      navigateWithQueryString({ url: redirectUrl, options: { replace: true } })
    } else if (isPosMembershipFlow && isMembershipSignupTerminalEnabled) {
      navigateWithQueryString({ url: '/membership/email-verification' })
    } else if (client?.isBoopUser) {
      handleFreeSignUp()
    } else {
      navigateWithQueryString({ url: '/membership/email-verification' })
    }
  }

  const onScroll = (event) => {
    if (
      event.target.scrollTop >
      event.target.scrollHeight - event.target.clientHeight - 20
    ) {
      setScrolled(true)
    }
  }

  const onBeforeBack = () => {
    if (viewOnly) {
      return
    }

    trackMembershipEnrollment('BACK_CLICKED', {
      stepName: 'terms',
      stepNumber: 6,
    })
  }

  return (
    <KioskScreen
      additionalLabel={
        isCurrentBusinessOmniChannel && !viewOnly ? (
          <PuiCheckbox
            checked={accepted}
            className={classes.checkbox}
            disabled={!scrolled}
            label={
              isCurrentBusinessOmniChannel
                ? t(
                    'Membership:OMNICHANNEL_WELLNESS_PLAN_TERMS_AND_CONDITIONS_ACCEPT',
                  )
                : t('Membership:WELLNESS_PLAN_TERMS_AND_CONDITIONS_ACCEPT')
            }
            onChange={() => {
              trackMembershipEnrollment('TERMS_CHECKBOX_CLICKED', {
                additionalProperties: {
                  accepted: !accepted,
                },
              })
              setAccepted(!accepted)
            }}
          />
        ) : null
      }
      classes={{
        title: classes.title,
      }}
      justifyContent="flex-start"
      proceedButtonDisabled={
        (!viewOnly &&
          (!scrolled || (isCurrentBusinessOmniChannel && !accepted))) ||
        isLoading
      }
      proceedButtonLabel={t('Common:CONTINUE_ACTION')}
      proceedButtonLoading={signUpIsLoading}
      title={t('Common:TERMS_AND_CONDITIONS')}
      onBeforeBack={onBeforeBack}
      onProceed={onProceed}
    >
      {isLoading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      ) : termsAndConditions?.html ? (
        <GlobalTermsAndConditions
          termsAndConditions={termsAndConditions.html}
          onScroll={onScroll}
        />
      ) : isCurrentBusinessOmniChannel ? (
        <OmnichannelWellnessPlanTermsAndConditions onScroll={onScroll} />
      ) : (
        <WellnessPlanTermsAndConditions onScroll={onScroll} />
      )}
    </KioskScreen>
  )
}

export default MembershipTermsAndConditionsScreen
